import { createContext, useContext, useState } from "react";

const translations = {
  en: {
    nav: {
      home: "Home",
      about: "About",
      skills: "Skills",
      projects: "Projects",
      company: "Company",
      contact: "Contact",
      cta: "Contact",
    },
    hero: {
      overline: "Fullstack Developer",
      headline: "Automation and integrations for financial operations",
      sub: "I build APIs, automate legacy workflows, and integrate AI into production systems. Based in Brazil, working remotely.",
      cta1: "View Projects",
      cta2: "WhatsApp",
      tagline1: "Automation and integrations",
      tagline2: "for financial operations.",
      brandName: "Pedro Lucas",
      cardHeading: "Software that runs in production.",
      cardDescription:
        "APIs, legacy system automation and AI applied to financial operations. From the WhatsApp bot to the negotiation portal, everything is built to work for real.",
      chat: {
        header: "Collections assistant",
        status: "online",
        messages: [
          { from: "bot", text: "Hi! We found an open balance on your account. Want to see negotiation options?" },
          { from: "user", text: "Yes, please." },
          { from: "bot", text: "You can settle in 3 interest-free installments. Should I send the payment slip?" },
        ],
      },
      badges: [
        { title: "Bot in production", sub: "Credit recovery" },
        { title: "Financial integration", sub: "Legacy systems" },
      ],
      speech: { title: "Speech Analytics", sub: "Analyzing call in real time", transcript: "\"...I'd like to negotiate the balance...\"" },
      ctaHeading: "Let's talk?",
      ctaDescription: "Open to freelance projects and full-time opportunities. Reach out by WhatsApp or take a look at the projects.",
    },
    about: {
      title: "About",
      p1: "I'm Pedro Lucas, a Fullstack Developer focused on automation, API development, and systems integration. Most of my work sits at the intersection of legacy infrastructure and modern tooling.",
      p2: "I've built speech analytics pipelines, debt negotiation platforms, WhatsApp bots with AI, and end-to-end process automation for financial operations. I care about systems that work reliably in production, not demos.",
      highlights: [
        "Legacy system integration",
        "Financial and credit operations",
        "Process automation at scale",
        "AI applied to real workflows",
      ],
    },
    skills: {
      title: "Technical stack",
      sub: "Technologies I use regularly in production environments.",
      groups: [
        {
          label: "Backend",
          items: ["Java", "Node.js", "Python", "REST APIs"],
        },
        {
          label: "Frontend",
          items: ["React", "TypeScript", "JavaScript"],
        },
        {
          label: "Data & infrastructure",
          items: ["SQL", "Jenkins / CI/CD", "Git"],
        },
        {
          label: "Quality & AI",
          items: ["QA / Testing", "AI Integration", "NLP"],
        },
      ],
    },
    projects: {
      title: "Selected projects",
      sub: "Work delivered for financial services and operations teams.",
      imageUrl: "/images/projects-timeline.jpg",
      imageAlt: "Abstract tech infrastructure visualization",
      items: [
        {
          id: "speech-analytics",
          order: 1,
          row: "top",
          title: "Speech Analytics",
          desc: "Real-time audio analysis pipeline for extracting business insights from customer calls.",
          tags: ["Java", "AI", "Queueing Systems"],
        },
        {
          id: "agreement-automation",
          order: 2,
          row: "bottom",
          title: "Agreement Maintenance Automation",
          desc: "End-to-end automation of operational agreement workflows, replacing manual processing across multiple systems.",
          tags: ["Python", "Automation", "APIs"],
        },
        {
          id: "dedicated-automation",
          order: 3,
          row: "top",
          title: "Advanced Dedicated Automation",
          desc: "Custom automation layer for critical business workflows with monitoring and error recovery.",
          tags: ["Node.js", "Python", "CI/CD"],
        },
        {
          id: "debt-portal",
          order: 4,
          row: "bottom",
          title: "Debt Negotiation Portal",
          desc: "Full-stack platform with financial system integrations for debt management and negotiation.",
          tags: ["React", "Java", "SQL"],
        },
        {
          id: "whatsapp-bot",
          order: 5,
          row: "top",
          title: "AI WhatsApp Bot — Credit Recovery",
          desc: "Production WhatsApp bot with AI for automated credit recovery conversations, integrated with backend financial systems.",
          tags: ["Node.js", "AI", "WhatsApp API"],
        },
      ],
    },
    company: {
      title: "AstravionTech",
      sub: "Founder & Technical Lead",
      p: "AstravionTech is my technology company. We build automation systems and web platforms for businesses that need reliable software, not slide decks.",
      cta: "Visit website",
    },
    contact: {
      title: "Get in touch",
      sub: "Open to freelance projects and full-time opportunities. Reach out by WhatsApp, email, or GitHub.",
      whatsapp: "WhatsApp",
      email: "Send Email",
      github: "GitHub",
    },
    footer: {
      by: "Pedro Lucas",
      rights: "All rights reserved.",
    },
  },
  pt: {
    nav: {
      home: "Início",
      about: "Sobre",
      skills: "Habilidades",
      projects: "Projetos",
      company: "Empresa",
      contact: "Contato",
      cta: "Contato",
    },
    hero: {
      overline: "Desenvolvedor Fullstack",
      headline: "Automação e integrações para operações financeiras",
      sub: "Desenvolvo APIs, automatizo fluxos em sistemas legados e integro IA em ambientes de produção. Baseado no Brasil, trabalho remotamente.",
      cta1: "Ver Projetos",
      cta2: "WhatsApp",
      tagline1: "Automação e integrações",
      tagline2: "para operações financeiras.",
      brandName: "Pedro Lucas",
      cardHeading: "Software que roda em produção.",
      cardDescription:
        "APIs, automação de sistemas legados e IA aplicada a operações financeiras. Do bot de WhatsApp ao portal de negociação, tudo construído para funcionar de verdade.",
      chat: {
        header: "Assistente de cobrança",
        status: "online",
        messages: [
          { from: "bot", text: "Olá! Identificamos uma pendência em aberto. Quer ver opções de negociação?" },
          { from: "user", text: "Sim, por favor." },
          { from: "bot", text: "Você pode quitar em 3x sem juros. Deseja receber o boleto?" },
        ],
      },
      badges: [
        { title: "Bot em produção", sub: "Recuperação de crédito" },
        { title: "Integração financeira", sub: "Sistemas legados" },
      ],
      speech: { title: "Speech Analytics", sub: "Analisando ligação em tempo real", transcript: "\"...gostaria de negociar o saldo...\"" },
      ctaHeading: "Vamos conversar?",
      ctaDescription: "Aberto a projetos freelance e oportunidades em tempo integral. Fale comigo pelo WhatsApp ou veja os projetos.",
    },
    about: {
      title: "Sobre",
      p1: "Sou Pedro Lucas, Desenvolvedor Fullstack com foco em automação, desenvolvimento de APIs e integração de sistemas. A maior parte do meu trabalho está na interface entre infraestrutura legada e ferramentas modernas.",
      p2: "Já construí pipelines de speech analytics, plataformas de negociação de dívidas, bots de WhatsApp com IA e automação ponta a ponta para operações financeiras. Priorizo sistemas que funcionam de forma confiável em produção.",
      highlights: [
        "Integração com sistemas legados",
        "Operações financeiras e de crédito",
        "Automação de processos em escala",
        "IA aplicada a fluxos reais",
      ],
    },
    skills: {
      title: "Stack técnica",
      sub: "Tecnologias que uso regularmente em ambientes de produção.",
      groups: [
        {
          label: "Backend",
          items: ["Java", "Node.js", "Python", "REST APIs"],
        },
        {
          label: "Frontend",
          items: ["React", "TypeScript", "JavaScript"],
        },
        {
          label: "Dados e infraestrutura",
          items: ["SQL", "Jenkins / CI/CD", "Git"],
        },
        {
          label: "Qualidade e IA",
          items: ["QA / Testing", "AI Integration", "NLP"],
        },
      ],
    },
    projects: {
      title: "Projetos selecionados",
      sub: "Trabalhos entregues para equipes de operações e serviços financeiros.",
      imageUrl: "/images/projects-timeline.jpg",
      imageAlt: "Visualização abstrata de infraestrutura tecnológica",
      items: [
        {
          id: "speech-analytics",
          order: 1,
          row: "top",
          title: "Speech Analytics",
          desc: "Pipeline de análise de áudio em tempo real para extrair insights de negócio a partir de ligações com clientes.",
          tags: ["Java", "IA", "Sistemas de Filas"],
        },
        {
          id: "agreement-automation",
          order: 2,
          row: "bottom",
          title: "Automação de Manutenção de Acordos",
          desc: "Automação ponta a ponta de fluxos operacionais de acordos, substituindo processamento manual entre múltiplos sistemas.",
          tags: ["Java", "Automação", "APIs"],
        },
        {
          id: "dedicated-automation",
          order: 3,
          row: "top",
          title: "Automação Dedicada Avançada",
          desc: "Camada de automação customizada para fluxos críticos de negócio, com monitoramento e recuperação de erros.",
          tags: ["Node.js", "Python", "CI/CD"],
        },
        {
          id: "debt-portal",
          order: 4,
          row: "bottom",
          title: "Portal de Negociação de Dívidas",
          desc: "Plataforma fullstack com integrações a sistemas financeiros para gestão e negociação de dívidas.",
          tags: ["React", "Java", "SQL"],
        },
        {
          id: "whatsapp-bot",
          order: 5,
          row: "top",
          title: "Bot WhatsApp com IA — Recuperação de Crédito",
          desc: "Bot de WhatsApp em produção com IA para conversas automatizadas de recuperação de crédito, integrado a sistemas financeiros.",
          tags: ["Node.js", "IA", "WhatsApp API"],
        },
      ],
    },
    company: {
      title: "AstravionTech",
      sub: "Fundador e Líder Técnico",
      p: "A AstravionTech é minha empresa de tecnologia. Construímos sistemas de automação e plataformas web para negócios que precisam de software confiável.",
      cta: "Visitar site",
    },
    contact: {
      title: "Contato",
      sub: "Aberto a projetos freelance e oportunidades em tempo integral. Escreva por WhatsApp, e-mail ou GitHub.",
      whatsapp: "WhatsApp",
      email: "Enviar E-mail",
      github: "GitHub",
    },
    footer: {
      by: "Pedro Lucas",
      rights: "Todos os direitos reservados.",
    },
  },
};

const LangContext = createContext(null);

export const LangProvider = ({ children }) => {
  const [lang, setLang] = useState("pt");
  const t = translations[lang];
  const toggle = () => setLang((l) => (l === "pt" ? "en" : "pt"));
  return (
    <LangContext.Provider value={{ lang, toggle, t }}>
      {children}
    </LangContext.Provider>
  );
};

export const useLang = () => useContext(LangContext);
